/**
 * Query-string handoff from sell submit → `/sell/success` (D7).
 * Fail-soft: missing/invalid params → generic success.
 */

import { myStuffPath } from "@/lib/my-stuff";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type SellSuccessHandoff = {
  itemIds: string[];
  count: number;
};

export function isSellSuccessItemId(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/** Build `/sell/success?ids=…` from created listing ids. */
export function buildSellSuccessHref(itemIds: readonly string[]): string {
  const ids = itemIds.map((id) => id.trim()).filter(isSellSuccessItemId);
  if (ids.length === 0) {
    return "/sell/success";
  }
  const params = new URLSearchParams();
  params.set("ids", ids.join(","));
  return `/sell/success?${params.toString()}`;
}

/** Parse success-page search params. Count falls back to ids length. */
export function parseSellSuccessHandoff(
  searchParams: URLSearchParams | { get: (key: string) => string | null },
): SellSuccessHandoff {
  const rawIds = searchParams.get("ids") ?? "";
  const itemIds = rawIds
    .split(",")
    .map((id) => id.trim())
    .filter(isSellSuccessItemId);

  const rawCount = searchParams.get("count");
  const parsedCount = rawCount ? Number.parseInt(rawCount, 10) : NaN;
  const count =
    Number.isFinite(parsedCount) && parsedCount > 0
      ? parsedCount
      : itemIds.length;

  return { itemIds, count };
}

/** Primary CTA destination — deep-link single listing into My Items when possible. */
export function resolveSellSuccessPrimaryHref(
  handoff: SellSuccessHandoff,
): string {
  if (handoff.itemIds.length === 1) {
    return `${myStuffPath("selling")}?item=${encodeURIComponent(handoff.itemIds[0])}`;
  }
  return myStuffPath("selling");
}
